import React, { useEffect, useState } from 'react';
import { db, rtdb } from '../firebase/client';
import { ref, onValue, off } from 'firebase/database';
import { doc, getDoc } from 'firebase/firestore';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

interface TrackingData {
    lat: number;
    lng: number;
    heading: number;
    speed: number;
    updatedAt: number;
}

const VERSION = "V2.6 - ULTRA_ROBUST_TRACKING";

// Leaflet icon fix for missing default icon in React
const defaultIcon = L.icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/3063/3063822.png', // A cute scooter icon
    iconSize: [46, 46],
    iconAnchor: [23, 23],
    popupAnchor: [0, -20],
    className: 'drop-shadow-xl transition-all duration-300'
});

// A component to automatically recenter the map when driver moves
const RecenterAutomatically = ({ lat, lng }: { lat: number, lng: number }) => {
    const map = useMap();
    useEffect(() => {
        if (lat && lng) {
            map.setView([lat, lng], map.getZoom(), {
                animate: true,
                duration: 1
            });
        }
    }, [lat, lng, map]);
    return null;
}

// Haversine formula to calculate distance between two points in km
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const normalizeLocation = (loc: any): { lat: number, lng: number } | null => {
    if (!loc) return null;
    
    try {
        // Handle Firestore GeoPoint or common lat/lng patterns
        let lat = loc.lat ?? loc.latitude ?? loc._lat ?? (typeof loc === 'object' ? loc.x : null) ?? null;
        let lng = loc.lng ?? loc.longitude ?? loc._lng ?? (typeof loc === 'object' ? loc.y : null) ?? null;
        
        // Handle string inputs
        if (typeof lat === 'string') lat = parseFloat(lat);
        if (typeof lng === 'string') lng = parseFloat(lng);

        if (lat !== null && lng !== null) {
            const nLat = Number(lat);
            const nLng = Number(lng);
            
            // Ensure they are valid numbers and not [0,0]
            if (!isNaN(nLat) && !isNaN(nLng) && Math.abs(nLat) > 0.0001) {
                return { lat: nLat, lng: nLng };
            }
        }
    } catch (e) {
        console.error("[TRACKING] Normalization error:", e);
    }
    console.warn("[TRACKING] Failed to normalize location:", loc);
    return null;
};

const LiveTrackingMap: React.FC<{
    orderId: string,
    clientLocation?: any, // Accept any to allow flexible normalization
    restaurantLocation?: any,
    livreurId?: string,
    fullHeight?: boolean,
    onEstimateChange?: (minutes: number | null) => void
}> = ({ orderId, clientLocation: propClientLocation, restaurantLocation: propRestaurantLocation, livreurId, fullHeight = false, onEstimateChange }) => {
    const [driverLocation, setDriverLocation] = useState<TrackingData | null>(null);
    const [estimate, setEstimate] = useState<number | null>(null);
    const [routePoints, setRoutePoints] = useState<[number, number][]>([]);
    const [lastReceivedAt, setLastReceivedAt] = useState<number>(Date.now());
    const [status, setStatus] = useState<'fresh' | 'stale' | 'offline'>('fresh');

    // Normalize input locations
    const clientLocation = React.useMemo(() => normalizeLocation(propClientLocation), [propClientLocation]);
    const restaurantLocation = React.useMemo(() => normalizeLocation(propRestaurantLocation), [propRestaurantLocation]);

    useEffect(() => {
        console.log(`[TRACKING ${VERSION}] Data State:`, {
            client: clientLocation ? "OK" : "MISSING",
            resto: restaurantLocation ? "OK" : "MISSING",
            driver: driverLocation ? "OK" : "WAITING_RTDB"
        });
    }, [clientLocation, restaurantLocation, driverLocation]);

    // Connectivity monitoring
    useEffect(() => {
        const interval = setInterval(() => {
            const age = (Date.now() - lastReceivedAt) / 1000;
            if (age > 60) setStatus('offline');
            else if (age > 25) setStatus('stale');
            else setStatus('fresh');
        }, 5000);
        return () => clearInterval(interval);
    }, [lastReceivedAt]);

    // Fetch road-based route from OSRM
    useEffect(() => {
        const fetchRoute = async () => {
            if (!driverLocation || !clientLocation) {
                console.log(`[TRACKING ${VERSION}] Skipping route fetch - missing driver or client`);
                return;
            }

            try {
                const dLat = Number(driverLocation.lat);
                const dLng = Number(driverLocation.lng);
                const cLat = Number(clientLocation.lat);
                const cLng = Number(clientLocation.lng);
                const rLat = restaurantLocation ? Number(restaurantLocation.lat) : null;
                const rLng = restaurantLocation ? Number(restaurantLocation.lng) : null;

                // Tiered logic
                let url;
                let isRestoIncluded = false;

                if (rLat !== null && rLng !== null && !isNaN(rLat) && !isNaN(rLng)) {
                    // Try 3-point route: Resto -> Driver -> Client
                    url = `https://router.project-osrm.org/route/v1/driving/${rLng},${rLat};${dLng},${dLat};${cLng},${cLat}?overview=full&geometries=geojson`;
                    isRestoIncluded = true;
                } else {
                    // Try 2-point route: Driver -> Client
                    url = `https://router.project-osrm.org/route/v1/driving/${dLng},${dLat};${cLng},${cLat}?overview=full&geometries=geojson`;
                }

                console.log(`[TRACKING ${VERSION}] Fetching OSRM (${isRestoIncluded ? '3-pt' : '2-pt'}): ${url}`);
                const response = await fetch(url);
                const data = await response.json();

                if (data.code === 'Ok' && data.routes?.[0]?.geometry?.coordinates) {
                    const allPoints: [number, number][] = data.routes[0].geometry.coordinates.map(
                        (coord: [number, number]) => [coord[1], coord[0]] as [number, number]
                    );
                    setRoutePoints(allPoints);
                    
                    const routeTime = data.routes[0].duration / 60; // in minutes
                    
                    // Intelligent buffer: +2 min only if far, +1 min if close, +0 if at destination
                    let totalTime;
                    const directDist = calculateDistance(dLat, dLng, cLat, cLng);
                    
                    if (directDist < 0.1) {
                         totalTime = 0; // Driver is at the customer
                    } else if (directDist < 0.5) {
                         totalTime = Math.ceil(routeTime); // Smallest estimate (1 min minimum)
                    } else {
                         totalTime = Math.round(routeTime + 1.5); // Normal buffer
                    }
                    
                    setEstimate(totalTime);
                    if (onEstimateChange) onEstimateChange(totalTime);
                    console.log(`[TRACKING ${VERSION}] OSRM Success. Direct Distance: ${directDist.toFixed(2)}km, Estimate: ${totalTime}min`);
                } else {
                    throw new Error(`OSRM invalid code: ${data.code}`);
                }
            } catch (error) {
                console.warn(`[TRACKING ${VERSION}] OSRM failed, using fallback:`, error);
                // Emergency Fallback: Straight line + Distance calculation
                if (driverLocation && clientLocation) {
                    setRoutePoints([[driverLocation.lat, driverLocation.lng], [clientLocation.lat, clientLocation.lng]]);
                    
                    const distInKm = calculateDistance(driverLocation.lat, driverLocation.lng, clientLocation.lat, clientLocation.lng);
                    
                    let fallbackEstimate;
                    if (distInKm < 0.1) {
                         fallbackEstimate = 0;
                    } else if (distInKm < 0.5) {
                         fallbackEstimate = 1;
                    } else {
                         fallbackEstimate = Math.round((distInKm / 30) * 60 + 2); // Assume 30km/h and 2 min buffer
                    }
                    
                    setEstimate(fallbackEstimate);
                    if (onEstimateChange) onEstimateChange(fallbackEstimate);
                    console.log(`[TRACKING ${VERSION}] Fallback applied. Estimate: ${fallbackEstimate}min`);
                }
            }
        };

        const timer = setTimeout(fetchRoute, 800);
        return () => clearTimeout(timer);
    }, [driverLocation?.lat, driverLocation?.lng, clientLocation?.lat, clientLocation?.lng, restaurantLocation?.lat, restaurantLocation?.lng, onEstimateChange]);

    // Live listening for driver location
    useEffect(() => {
        if (!orderId) return;
        const trackingRef = ref(rtdb, `tracking/${orderId}`);

        const unsubscribe = onValue(trackingRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                if (data && typeof data.lat === 'number' && typeof data.lng === 'number') {
                    setDriverLocation(data);
                    setLastReceivedAt(Date.now());
                }
            }
        });

        return () => off(trackingRef, 'value', unsubscribe);
    }, [orderId]);

    // Fetch initial location from Firestore
    useEffect(() => {
        if (!livreurId || driverLocation) return;
        const fetchInitial = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', livreurId));
                if (userDoc.exists()) {
                    const initialLoc = normalizeLocation(userDoc.data().location);
                    if (initialLoc) {
                        setDriverLocation({
                            lat: initialLoc.lat,
                            lng: initialLoc.lng,
                            heading: 0,
                            speed: 0,
                            updatedAt: Date.now()
                        });
                    }
                }
            } catch (err) { console.error(err); }
        };
        fetchInitial();
    }, [livreurId]);

    if (!driverLocation) {
        return (
            <div className="w-full h-48 bg-white rounded-3xl flex flex-col items-center justify-center border border-gray-100 shadow-sm relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-orange-50 opacity-30 animate-pulse"></div>
                <div className="text-center px-6 relative z-10">
                    <p className="text-gray-800 font-black text-sm mb-1 uppercase tracking-tight">Recherche du livreur...</p>
                    <p className="text-gray-500 font-bold text-[11px] leading-tight">La carte s'affichera dès que le signal sera capté.</p>
                </div>
            </div>
        );
    }

    const clientIcon = L.icon({
        iconUrl: 'https://cdn-icons-png.flaticon.com/512/2555/2555572.png',
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        className: 'drop-shadow-lg'
    });

    const restaurantIcon = L.icon({
        iconUrl: 'https://cdn-icons-png.flaticon.com/512/433/433102.png',
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        className: 'drop-shadow-lg'
    });

    return (
        <div className={`w-full ${fullHeight ? 'h-full' : 'h-64'} rounded-3xl overflow-hidden shadow-2xl border border-white relative`}>
            <MapContainer
                center={[driverLocation.lat, driverLocation.lng]}
                zoom={15}
                className="w-full h-full"
                zoomControl={false}
            >
                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                
                {/* Driver Marker */}
                <Marker position={[driverLocation.lat, driverLocation.lng]} icon={defaultIcon} />

                {/* Client Marker */}
                {clientLocation && (
                    <Marker position={[clientLocation.lat, clientLocation.lng]} icon={clientIcon} />
                )}

                {/* Restaurant Marker */}
                {restaurantLocation && (
                    <Marker position={[restaurantLocation.lat, restaurantLocation.lng]} icon={restaurantIcon} />
                )}

                {/* Road Traces */}
                {clientLocation && routePoints.length > 0 && (() => {
                    let minDistance = Infinity;
                    let splitIndex = 0;
                    routePoints.forEach((p, i) => {
                        const dist = calculateDistance(p[0], p[1], driverLocation.lat, driverLocation.lng);
                        if (dist < minDistance) {
                            minDistance = dist;
                            splitIndex = i;
                        }
                    });

                    const pastSegment = routePoints.slice(0, splitIndex + 1);
                    const futureSegment = routePoints.slice(splitIndex);

                    return (
                        <>
                            {/* Past Segment - Dotted Orange */}
                            {pastSegment.length > 1 && (
                                <Polyline 
                                    positions={pastSegment} 
                                    pathOptions={{ color: '#f97316', weight: 3, opacity: 0.4, dashArray: '5, 10' }} 
                                />
                            )}
                            {/* Future Segment - Premium Glow Red */}
                            {futureSegment.length > 1 && (
                                <>
                                    <Polyline 
                                        positions={futureSegment} 
                                        pathOptions={{ color: '#dc2626', weight: 10, opacity: 0.2 }} 
                                    />
                                    <Polyline 
                                        positions={futureSegment} 
                                        pathOptions={{ color: '#dc2626', weight: 5, opacity: 0.8 }} 
                                    />
                                    <Polyline 
                                        positions={futureSegment} 
                                        pathOptions={{ color: '#ffffff', weight: 1.5, opacity: 0.8 }} 
                                    />
                                </>
                            )}
                        </>
                    );
                })()}

                {/* Fallback straight line if road routing is empty but we have locs */}
                {clientLocation && routePoints.length === 0 && (
                    <Polyline 
                        positions={[[driverLocation.lat, driverLocation.lng], [clientLocation.lat, clientLocation.lng]]}
                        pathOptions={{ color: '#dc2626', weight: 4, opacity: 0.5, dashArray: '10, 10' }}
                    />
                )}

                <RecenterAutomatically lat={driverLocation.lat} lng={driverLocation.lng} />
            </MapContainer>

            {/* Float Info */}
            <div className="absolute top-4 right-4 z-[400] flex flex-col gap-2">
                <div className="bg-white/90 backdrop-blur px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${status === 'fresh' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                    <span className="text-[10px] font-black uppercase text-gray-700">Direct</span>
                </div>
                {estimate !== null && (
                    <div className="bg-green-600 text-white px-4 py-2 rounded-2xl shadow-xl flex flex-col items-center">
                        <span className="text-[9px] font-black uppercase tracking-widest opacity-80">Arrivée</span>
                        <span className="text-sm font-black leading-none">{estimate} min</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveTrackingMap;
